/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import classNames from 'classnames'
import { Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { useConfirmation } from '../ConfirmationContext'
import { Button } from 'app/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'app/components/ui/command'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog'
import { FieldLabel } from 'app/components/ui/field'
import { Input } from 'app/components/ui/input'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import Dots from 'app/components/shared/styled/Dots'
import { tagSwatchClasses } from 'app/lib/chipColors'
import { gqlQuery } from 'app/lib/graphqlClient'
import { TAG_COLORS_QUERY } from './graphql'
import { dedupeTagsByName } from './tagUtils'
import { TagColorPicker } from './TagColorPicker'
import { bg } from 'lib/background'
import { logError, logWarn } from 'lib/logging'
import {
  getActivityBuiltInTagColor,
  getActivityBuiltInTags,
  getContactBuiltInTagColor,
  getContactBuiltInTags,
} from 'lib/getBuiltInTags'

const ENTITY_MAP: Record<string, string> = {
  activities: `Activity`,
  contacts: `Contact`,
  locations: `Location`,
  calls: `Call`,
  workspaces: `Workspace`,
  forms: `Form`,
}

function builtInTagsForEntity(entity?: string): { name: string; color?: string | null }[] {
  if (entity === `contacts` || entity === `Contact`) {
    return getContactBuiltInTags().map((name) => ({ name, color: getContactBuiltInTagColor(name) }))
  }
  if (entity === `activities` || entity === `Activity`) {
    return getActivityBuiltInTags().map((name) => ({ name, color: getActivityBuiltInTagColor(name) }))
  }
  return []
}

const createSchema = z.object({
  name: z.string().trim().min(1, { message: `A name is required` }),
  color: z.string().min(1, { message: `Tag Color is required` }),
})

type AddTagShadcnProps = {
  shouldConfirmNewTag?: boolean
  asLabel?: boolean
  /** Text shown next to the "+" when rendered `asLabel`. Defaults to "Add tag". */
  label?: string
  className?: string
  entity?: string
  workspaceId?: string
  onAdd: (tag: string, color?: string) => void
  forceOptionsOpen?: boolean
  customButton?: React.ReactNode
  optionsContainerClassName?: string
  containerRef?: React.RefObject<HTMLElement | null>
  popoverOpen?: boolean
  onPopoverOpenChange?: (open: boolean) => void
  /** Also pass the tag's colour to onAdd when an existing tag is selected (not just on create). */
  passColorOnSelect?: boolean
  /** Position the dropdown against this element instead of the trigger, so collision
   *  flips open above/below the whole element rather than the (possibly invisible) trigger. */
  anchorRef?: React.RefObject<HTMLElement | null>
}

export default function AddTagShadcn({
  shouldConfirmNewTag = true,
  asLabel = false,
  label = `Add tag`,
  className = ``,
  entity,
  workspaceId,
  onAdd,
  forceOptionsOpen,
  customButton,
  containerRef,
  popoverOpen: externalOpen,
  onPopoverOpenChange,
  passColorOnSelect = false,
  anchorRef,
}: AddTagShadcnProps) {
  const [confirm] = useConfirmation()
  const [internalOpen, setInternalOpen] = useState(forceOptionsOpen ?? false)
  const popoverOpen = externalOpen ?? internalOpen
  const setPopoverOpen = onPopoverOpenChange ?? setInternalOpen
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedColor, setSelectedColor] = useState<string>(`bg-tags-silver`)
  const [tagName, setTagName] = useState<string>(``)
  const [nameError, setNameError] = useState<string>(``)
  const [search, setSearch] = useState(``)
  const searchRef = useRef(``)

  const [allTags, setAllTags] = useState<{ name: string; color?: string | null }[]>([])
  const [tagsLoading, setTagsLoading] = useState(false)

  const fetchTags = async () => {
    setTagsLoading(true)
    try {
      // Org/default TagColors (isOrgTag) apply to every workspace. Workspace-only
      // scope would hide them. Workspace users still cannot see org TagColors —
      // the API forces their workspaceId must-clause.
      const parts = [
        entity ? `entity:${ENTITY_MAP[entity] ?? entity}` : undefined,
        workspaceId ? `(workspaceId:${workspaceId} OR isOrgTag:true)` : undefined,
      ].filter(Boolean)
      const searchFilter = parts.length ? parts.join(` AND `) : undefined
      const result = await gqlQuery(TAG_COLORS_QUERY, { search: searchFilter }, { requestPolicy: `network-only` })
      // Built-in tags are code defaults (not always present as TagColor rows). A new
      // workspace with no TagColors would otherwise show an empty picker.
      setAllTags(dedupeTagsByName([...(result.data?.tagColors?.hits ?? []), ...builtInTagsForEntity(entity)]))
    } catch (err) {
      logError(`Failed to fetch tags`, err)
      setAllTags(builtInTagsForEntity(entity))
    } finally {
      setTagsLoading(false)
    }
  }

  // Instant client-side partial matching
  const displayTags = search ? allTags.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())) : allTags

  useEffect(() => {
    if (popoverOpen) {
      setTagsLoading(true)
      bg(fetchTags())
    }
  }, [popoverOpen])

  useEffect(() => {
    if (forceOptionsOpen !== undefined) setPopoverOpen(forceOptionsOpen)
  }, [forceOptionsOpen])

  const handleSearch = (value: string) => {
    setSearch(value)
    searchRef.current = value
  }

  const handleSelectTag = (name: string, color?: string | null) => {
    if (name) {
      onAdd(name, passColorOnSelect ? (color ?? undefined) : undefined)
    } else {
      logWarn(`Option selected has no value`)
    }
    setSearch(``)
  }

  const handleCreateNewTag = () => {
    const text = searchRef.current
    if (shouldConfirmNewTag) {
      if (!text?.trim()) return
      confirm({
        title: `Creating new tag`,
        description: `Are you sure you want to create a new tag called '${text}'?`,
        variant: `info`,
      }).then(() => {
        setTagName(text.trim())
        setPopoverOpen(false)
        setDialogOpen(true)
      })
    } else {
      setTagName(text.trim())
      setPopoverOpen(false)
      setDialogOpen(true)
    }
  }

  const handleCloseDialog = () => {
    setSelectedColor(`bg-tags-silver`)
    setNameError(``)
    setDialogOpen(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const result = createSchema.safeParse({ name: tagName, color: selectedColor })
    if (!result.success) {
      const err = result.error.flatten().fieldErrors.name?.[0]
      if (err) setNameError(err)
      return
    }
    onAdd(result.data.name, result.data.color)
    setAllTags((prev) =>
      prev.some((t) => t.name === result.data!.name)
        ? prev
        : [{ name: result.data!.name, color: result.data!.color }, ...prev],
    )
    handleCloseDialog()
    setPopoverOpen(true)
  }

  const isExternalPopover = !!onPopoverOpenChange

  const trigger = (
    <PopoverTrigger asChild>
      {customButton || (
        <button className={classNames(asLabel ? `text-ui-sm text-strong` : `tag-add-btn`, `outline-none`, className)}>
          + {asLabel && ` ${label}`}
        </button>
      )}
    </PopoverTrigger>
  )

  const content = (
    <PopoverContent
      side="bottom"
      align="start"
      className="bg-surface-raised p-0"
      // Track the container's width but never shrink below the w-72 default — tag
      // search needs the room even when the container (e.g. a narrow table column) doesn't.
      style={{ minWidth: containerRef?.current?.offsetWidth ? `${containerRef.current.offsetWidth}px` : undefined }}
      onCloseAutoFocus={(e) => e.preventDefault()}
    >
      <Command shouldFilter={false} className="bg-surface-raised">
        <CommandInput placeholder="Search tags..." value={search} onValueChange={handleSearch} />
        <CommandList>
          {tagsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Dots />
            </div>
          ) : (
            <>
              <CommandEmpty>No tags found.</CommandEmpty>
              {/* TODO: key={search} is a workaround for cmdk not clearing its internal item cache — find a proper fix */}
              <CommandGroup heading="Tags" key={search}>
                {displayTags.map((tag) => (
                  <CommandItem key={tag.name} value={tag.name} onSelect={() => handleSelectTag(tag.name, tag.color)}>
                    <span
                      className={classNames(
                        `h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-strong`,
                        tagSwatchClasses(tag.color),
                      )}
                    />
                    <span>{tag.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
        {/* Anchored to the bottom of the popover so it stays visible while the tag list scrolls. */}
        <div className="border-t border-default p-1">
          <button
            type="button"
            onClick={handleCreateNewTag}
            disabled={tagsLoading}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-(--menu-hover) hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            <span>Create new tag{search.trim() ? ` “${search.trim()}”` : ``}</span>
          </button>
        </div>
      </Command>
    </PopoverContent>
  )

  return (
    <>
      {isExternalPopover ? (
        <>
          {trigger}
          {anchorRef && <PopoverAnchor virtualRef={anchorRef} />}
          {content}
        </>
      ) : (
        <Popover
          open={popoverOpen}
          onOpenChange={(open) => {
            if (!forceOptionsOpen || open) setPopoverOpen(open)
          }}
        >
          {trigger}
          {anchorRef && <PopoverAnchor virtualRef={anchorRef} />}
          {content}
        </Popover>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseDialog()
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create New Tag</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <FieldLabel>Tag Name</FieldLabel>
              <Input
                value={tagName}
                onChange={(e) => {
                  setTagName(e.target.value)
                  if (nameError) setNameError(``)
                }}
                placeholder="Enter tag name"
              />
              {nameError && <p className="text-sm text-red-600">{nameError}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <FieldLabel>Tag Color</FieldLabel>
              <TagColorPicker value={selectedColor} onChange={setSelectedColor} previewName={tagName} />
            </div>
            <DialogFooter>
              <Button type="button" variant="white" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
